import { useEffect, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { FaCalendar, FaMapMarkerAlt, FaBrain, FaExpand, FaCode, FaServer, FaGraduationCap, FaBuilding } from 'react-icons/fa';

const Experience = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  const experiences = [
    {
      title: 'Industrial Trainee',
      company: 'Intel Unnati Industrial Training',
      period: '2024',
      location: 'Remote Workspace',
      description: [
        'Developed AI-integrated web applications using React and advanced ML frameworks.',
        'Implemented computer vision solutions and model inferencing pipelines for industrial environments.',
        'Collaborated with senior industry mentors on optimizing deep learning models.',
        'Gained hands-on proficiency with Intel-specific software tools and acceleration libraries.',
      ],
      icon: FaBuilding,
    },
    {
      title: 'AI & Data Science Student',
      company: 'Karunya University',
      period: '2023 - Present',
      location: 'Coimbatore, India',
      description: [
        'B.Tech candidate in Artificial Intelligence & Data Science with structural honors.',
        'Specialized coursework in Machine Learning architectures, Deep Learning systems, and Computer Vision.',
        'Active participant and core engineer in national engineering hackathons.',
        'Researching custom computer vision solutions for automated industrial compliance.',
      ],
      icon: FaGraduationCap,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 25, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };

  const renderBrackets = () => (
    <>
      <span className="reticle-bracket top-0 left-0 border-t border-l border-white-pure group-hover:translate-x-0.5 group-hover:translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket top-0 right-0 border-t border-r border-white-pure group-hover:-translate-x-0.5 group-hover:translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket bottom-0 left-0 border-b border-l border-white-pure group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket bottom-0 right-0 border-b border-r border-white-pure group-hover:-translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
    </>
  );

  return (
    <section
      id="experience"
      ref={sectionRef}
      className="section-padding relative overflow-hidden bg-void border-t border-grey-800"
    >
      <div className="container-custom relative z-10">
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="mb-20 text-left border-l-2 border-white-primary pl-6"
        >
          <motion.span
            variants={itemVariants}
            className="inline-block text-[9px] font-mono font-bold tracking-[0.35em] uppercase text-grey-400 mb-2"
          >
            04 // CAREER_CHRONOLOGY
          </motion.span>
          <motion.h2
            variants={itemVariants}
            className="text-3xl sm:text-5xl md:text-6xl font-display font-black"
          >
            WORK // <span className="chrome-text">EXPERIENCE</span>
          </motion.h2>
        </motion.div>

        {/* Timeline */}
        <div className="timeline max-w-3xl mx-auto relative">
          {/* Vertical Technical Line */}
          <div className="timeline-line absolute left-8 top-0 bottom-0 w-[1px] bg-grey-800 hidden md:block" />

          <motion.div 
            className="space-y-12"
            variants={containerVariants}
            initial="hidden"
            animate={controls}
          >
            {experiences.map((exp, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="timeline-item relative"
              >
                {/* Timeline Square Node */}
                <div className="absolute left-8 top-7 w-3 h-3 -ml-1.5 bg-void border border-white-pure rotate-45 hidden md:block z-10" />
                
                {/* Content Card */}
                <div className="md:ml-20 text-left">
                  <div className="card group cursor-pointer border border-grey-800 bg-elevated p-6 relative">
                    {renderBrackets()}

                    {/* Header */}
                    <div className="mb-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-7 h-7 bg-grey-800 border border-grey-600 flex items-center justify-center text-white-pure">
                          <exp.icon className="text-[10px]" />
                        </div>
                        <h3 className="text-lg font-display font-bold group-hover:text-white-pure transition-colors">
                          {exp.title}
                        </h3>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-[10px] font-mono text-grey-400 uppercase tracking-widest pl-10 md:pl-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-grey-600 font-bold">// COMPANY:</span>
                          <span>{exp.company}</span>
                        </div>
                        <span className="hidden sm:inline text-grey-800">|</span>
                        <div className="flex items-center gap-1.5">
                          <FaCalendar className="text-grey-600 text-xs" />
                          <span className="text-white-pure font-bold number-readout">{exp.period}</span>
                        </div>
                        <span className="hidden sm:inline text-grey-800">|</span>
                        <div className="flex items-center gap-1.5">
                          <FaMapMarkerAlt className="text-grey-600 text-xs" />
                          <span>{exp.location}</span>
                        </div>
                      </div>
                    </div>

                    {/* Description bullet nodes */}
                    <ul className="space-y-2.5 border-t border-grey-800/60 pt-4">
                      {exp.description.map((item, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-xs text-grey-400 leading-relaxed font-sans">
                          <span className="text-white-pure mt-1 text-[8px] font-bold">✦</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Competencies segment */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate={controls}
          className="mt-20 text-center"
        >
          <div className="bg-elevated border border-grey-800 p-8 max-w-2xl mx-auto">
            <h3 className="text-xs font-mono font-bold text-grey-400 uppercase tracking-[0.2em] mb-6">CORE_COMPETENCIES_LOGGED</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { name: 'AI/ML Pipelines', icon: FaBrain },
                { name: 'Computer Vision', icon: FaExpand },
                { name: 'Full Stack Dev', icon: FaCode },
                { name: 'System Scaling', icon: FaServer },
              ].map((skill, index) => (
                <div
                  key={index}
                  className="bg-void border border-grey-800 p-4 hover:border-grey-600 transition-all duration-250 cursor-pointer flex flex-col items-center justify-center group relative"
                >
                  {renderBrackets()}
                  <div className="w-10 h-10 bg-void border border-grey-800 group-hover:border-grey-600 flex items-center justify-center mb-3">
                    <skill.icon className="text-xs text-white-primary group-hover:text-white-pure transition-colors" />
                  </div>
                  <p className="text-[9px] uppercase tracking-widest font-mono font-bold text-grey-400 group-hover:text-white-pure transition-colors">{skill.name}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;
